import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MessageCircle, Check, X, Zap } from "lucide-react";

/**
 * BotCommentsAdminTab — moderation queue for kid-bot comments.
 *
 * Bots drop cheerful comments on kid posts every few minutes. This tab
 * lets an admin review them before they're surfaced publicly. Defaults
 * to the "pending" view; hit "Approve all" when nothing looks iffy.
 */
export default function BotCommentsAdminTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/kids/bot-comments?pending_only=${pendingOnly}`);
      setRows(r.data?.comments || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pendingOnly]);

  const approve = async (c) => {
    try {
      await api.post(`/admin/kids/bot-comments/${c.id}/approve`);
      setRows((xs) => xs.filter((x) => x.id !== c.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (c) => {
    try {
      await api.delete(`/admin/kids/bot-comments/${c.id}`);
      setRows((xs) => xs.filter((x) => x.id !== c.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const approveAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/kids/bot-comments/approve-all");
      toast.success(`Approved ${r.data.approved} comments`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="bot-comments-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-fuchsia-400" /> Bot Comments Queue
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Review cheerful bot reactions before they're surfaced publicly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-white/70 flex items-center gap-2">
            <input type="checkbox" data-testid="bc-pending-toggle" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
            Pending only
          </label>
          <button
            data-testid="bc-approve-all"
            onClick={approveAll}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm px-3 py-2 rounded-lg inline-flex items-center gap-1 disabled:opacity-60"
          ><Zap className="w-4 h-4" /> Approve all pending</button>
        </div>
      </div>

      {loading && <div className="text-white/50 text-sm">Loading comments…</div>}

      {!loading && rows.length === 0 && (
        <div data-testid="bc-empty" className="text-white/60 text-sm bg-black/30 border border-dashed border-white/10 rounded-xl p-8 text-center">
          <div className="text-4xl mb-2">🎉</div>
          All clear! No bot comments waiting.
        </div>
      )}

      {rows.length > 0 && (
        <div data-testid="bc-list" className="space-y-2">
          {rows.map((c) => (
            <div key={c.id} data-testid={`bc-row-${c.id}`} className="bg-black/40 border border-white/10 rounded-xl p-3 flex items-center gap-3">
              <div className="text-2xl shrink-0">{c.author_avatar || "⭐"}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-bold">{c.author_name} <span className="text-white/40 text-xs">on video {c.video_id?.slice(0, 10)}…</span></div>
                <div className="text-white/90 text-sm mt-0.5">{c.text}</div>
                <div className="text-white/40 text-[10px] mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              {c.approved
                ? <span className="text-emerald-300 text-xs font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full">Approved</span>
                : null}
              <button
                data-testid={`bc-approve-${c.id}`}
                onClick={() => approve(c)}
                className="text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black px-3 py-1.5 rounded-full inline-flex items-center gap-1"
              ><Check className="w-3 h-3" /> Approve</button>
              <button
                data-testid={`bc-delete-${c.id}`}
                onClick={() => del(c)}
                className="text-xs font-bold bg-red-500/80 hover:bg-red-500 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
              ><X className="w-3 h-3" /> Delete</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
